import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import { GROUP_STATUS, GROUP_TYPE, metaFor, type VisitorGroup } from "@/lib/adminSystem";
import { formatRupees } from "@/pages/admin/format";

const columns: Column<VisitorGroup>[] = [
  {
    key: "name",
    header: "Group",
    primary: true,
    render: (row) => (
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
        <p className="text-xs text-foreground-500 mt-0.5">
          {row.contact_name || "—"} · {row.location || "—"}
        </p>
      </div>
    ),
  },
  { key: "type", header: "Type", render: (row) => <StatusPill {...metaFor(GROUP_TYPE, row.group_type)} /> },
  { key: "size", header: "Size", render: (row) => <span className="text-sm text-foreground-700">{row.size}</span> },
  { key: "visits", header: "Visits", render: (row) => <span className="text-sm text-foreground-700">{row.total_visits}</span> },
  {
    key: "spend",
    header: "Lifetime spend",
    render: (row) => (
      <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
        {formatRupees(row.total_spend)}
      </span>
    ),
  },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(GROUP_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<VisitorGroup> = {
  table: "visitor_groups",
  orderBy: "total_spend",
  eyebrow: "Customers",
  title: "Visitor Groups",
  subtitle: "Schools, colleges, corporates and community groups that visit — their contacts, sizes and lifetime value in one place.",
  searchPlaceholder: "Search groups, contacts, locations…",
  searchKeys: ["name", "contact_name", "contact_email", "location"],
  filters: [
    { key: "group_type", label: "Type", options: optionsFrom(GROUP_TYPE) },
    { key: "status", label: "Status", options: optionsFrom(GROUP_STATUS) },
  ],
  statusMap: GROUP_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Total groups", value: rows.length, icon: "ri-group-line", tone: "primary" },
    { label: "Active", value: rows.filter((row) => row.status === "active").length, icon: "ri-checkbox-circle-line", tone: "primary" },
    { label: "Visitors hosted", value: rows.reduce((sum, row) => sum + Number(row.size || 0), 0), icon: "ri-user-3-line", tone: "secondary" },
    { label: "Lifetime spend", value: formatRupees(rows.reduce((sum, row) => sum + Number(row.total_spend || 0), 0)), icon: "ri-money-rupee-circle-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "name", label: "Group name" },
    { key: "group_type", label: "Type", type: "select", options: optionsFrom(GROUP_TYPE) },
    { key: "contact_name", label: "Contact name" },
    { key: "contact_email", label: "Contact email" },
    { key: "contact_phone", label: "Contact phone" },
    { key: "size", label: "Group size", type: "number" },
    { key: "location", label: "Location" },
    { key: "status", label: "Status", type: "select", options: optionsFrom(GROUP_STATUS) },
    { key: "notes", label: "Notes", type: "textarea" },
  ],
  createFields: [
    { key: "name", label: "Group name" },
    { key: "group_type", label: "Type", type: "select", options: optionsFrom(GROUP_TYPE) },
    { key: "contact_name", label: "Contact name" },
    { key: "contact_email", label: "Contact email" },
    { key: "contact_phone", label: "Contact phone" },
    { key: "size", label: "Group size", type: "number" },
    { key: "location", label: "Location" },
  ],
  newDefaults: {
    name: "",
    group_type: "school",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    size: 0,
    location: "",
    total_visits: 0,
    total_bookings: 0,
    total_spend: 0,
    status: "active",
    notes: "",
  },
  emptyTitle: "No visitor groups yet",
  emptyMessage: "Schools, corporates, families and clubs you host will appear here.",
  rowTitle: (row) => row.name,
};

export default function AdminVisitorGroups() {
  return <ResourceModule config={config} />;
}